import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import CareIcon from "@/CAREUI/icons/CareIcon";

import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import Page from "@/components/Common/Page";
import SearchInput from "@/components/Common/SearchInput";
import {
  CardGridSkeleton,
  TableSkeleton,
} from "@/components/Common/SkeletonLoading";
import UserListAndCardView from "@/components/Users/UserListAndCard";

import useFilters from "@/hooks/useFilters";

import query from "@/Utils/request/query";
import { useView } from "@/Utils/useView";
import facilityApi from "@/types/facility/facilityApi";

export default function FacilityUsers(props: { facilityId: string }) {
  const { t } = useTranslation();
  const { qParams, updateQuery, Pagination, resultsPerPage } = useFilters({
    limit: 15,
    disableCache: true,
  });
  const [activeTab, setActiveTab] = useView("users", "card");

  const { facilityId } = props;

  let usersList: React.ReactNode = <></>;

  const { data: userListData, isFetching: userListFetching } = useQuery({
    queryKey: ["facilityUsers", facilityId, qParams, resultsPerPage],
    queryFn: query.debounced(facilityApi.getUsers, {
      pathParams: { facilityId },
      queryParams: {
        username: qParams.username,
        limit: resultsPerPage,
        offset: (qParams.page - 1) * resultsPerPage,
      },
    }),
    enabled: !!facilityId,
  });

  if (userListFetching || !userListData) {
    usersList =
      activeTab === "card" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          <CardGridSkeleton count={6} />
        </div>
      ) : (
        <TableSkeleton count={7} />
      );
  } else {
    usersList = (
      <div>
        <UserListAndCardView
          users={userListData?.results ?? []}
          activeTab={activeTab === "card" ? "card" : "list"}
        />
        <Pagination totalCount={userListData.count} />
      </div>
    );
  }

  return (
    <Page title={t("users_management")} hideTitleOnPage>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t("users_management")}
          </h1>
          <Badge
            className="bg-purple-50 text-purple-700 mt-2 text-sm font-medium rounded-xl px-3 w-max"
            variant="outline"
          >
            {userListFetching
              ? t("loading")
              : t("entity_count", {
                  count: userListData?.count ?? 0,
                  entity: "User",
                })}
          </Badge>
        </div>
        {/* AayushNet Logo - Right side */}
        <div className="flex items-center gap-0 shrink-0">
          <img
            src="/images/aayushnet_logo_light.png"
            className="h-12"
            alt="AayushNet logo"
          />
          <h1
            className="text-2xl font-black tracking-wider"
            style={{
              fontFamily: "'Zen Kaku Gothic Antique', sans-serif",
            }}
          >
            <span
              style={{
                background:
                  "linear-gradient(to bottom, #5ba3ff 0%, #002d6b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                paddingBottom: "2px",
              }}
            >
              Aayush
            </span>
            <span
              style={{
                background:
                  "linear-gradient(to bottom, #66ff99 0%, #1a7a3f 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                paddingBottom: "2px",
              }}
            >
              Net
            </span>
          </h1>
        </div>
      </div>
      <hr className="mt-4 border-gray-200" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 m-5 ml-0">
        <SearchInput
          options={[
            {
              key: "username",
              type: "text",
              placeholder: t("search_by_name_or_username"),
              value: qParams.username || "",
              display: t("username"),
            },
          ]}
          onSearch={(key, value) =>
            updateQuery({
              [key]: value || undefined,
            })
          }
          className="w-full max-w-sm"
        />
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as "card" | "list")}
        >
          <TabsList className="flex">
            <TabsTrigger value="card" id="user-card-view">
              <CareIcon icon="l-credit-card" className="text-lg" />
              <span>{t("card")}</span>
            </TabsTrigger>
            <TabsTrigger value="list" id="user-list-view">
              <CareIcon icon="l-list-ul" className="text-lg" />
              <span>{t("list")}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="overflow-x-auto overflow-y-hidden">{usersList}</div>
    </Page>
  );
}
